/* eslint-disable */
import { useContext, useEffect, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { LocalizationContext } from "../../App";
import { Formatters } from "../../common/helpers/formatters";
import { InfoCircle, PowerOutline, Warning } from "../../common/icons";
import { Modal } from "../../common/Modal";
import { ProductContext } from "../../context/ProductContext";
import { PolicyStatus } from "../../models/enum/PolicyStatus";
import { Policy, PolicyWithTerminationInfo } from "../../models/policy";
import { UpdatePolicyStatusRequest } from "../../models/request/UpdatePolicyStatusRequest";
import { useUpdatePolicyStatusMutation } from "../../store/policiesSlice";

export const PolicyCancellation = () => {
  const l10n = useContext(LocalizationContext);
  const { productIdToTypeKeyDict } = useContext(ProductContext);
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { cancellablePolicies }: { cancellablePolicies?: PolicyWithTerminationInfo[] } =
    state ?? {};
  const [policiesToDisplay, setPoliciesToDisplay] = useState<PolicyWithTerminationInfo[]>(
    cancellablePolicies?.filter((p) => p.statusId !== PolicyStatus.Terminated) ?? []
  );
  const [policyToTerminate, setPolicyToTerminate] = useState<PolicyWithTerminationInfo | undefined>(
    undefined
  );
  const [
    updatePolicyStatus,
    {
      isError: isTerminationFailed,
      isLoading: isTerminationLoading,
      isSuccess: isTerminationSuccess,
    },
  ] = useUpdatePolicyStatusMutation();

  useEffect(() => {
    /**
     * Prevent direct navigation via hitting the URL in browser. (This page is only
     * accessible via the Action Menu, which will pass it a state object.)
     */
    if (state === undefined || state === null || state === "") {
      navigate("/");
    }
  }, [pathname, state]);

  useEffect(() => {
    if (policyToTerminate && !isTerminationLoading && isTerminationSuccess) {
      if (policyToTerminate?.termination?.isImmediate) {
        // Remove terminated policy from UI
        setPoliciesToDisplay(policiesToDisplay.filter((p) => p.id !== policyToTerminate.id));
      } else {
        // Set a termination date to be displayed on the policy's UI element
        const terminatedPolicyIndex = policiesToDisplay.findIndex(
          (p) => p.id === policyToTerminate?.id
        );
        if (terminatedPolicyIndex > -1) {
          policiesToDisplay[terminatedPolicyIndex].policyDetails.terminationDate =
            policyToTerminate.termination?.earliestExecutionDate;
        }
      }
      setTimeout(() => {
        closeModal();
      }, 1000);
    }
  }, [isTerminationLoading, isTerminationSuccess]);

  const terminatePolicy = (policy: PolicyWithTerminationInfo) => {
    const updatePolicyStatusRequest: UpdatePolicyStatusRequest = {
      policyIdentifier: policy.uniqueIdentifier,
      targetStatusId: PolicyStatus.Terminated,
      targetSubstatusStatusId: policy.termination!.subStatusId,
      executionDateTime: policy.termination?.earliestExecutionDate,
    };

    updatePolicyStatus(updatePolicyStatusRequest);
  };

  const closeModal = () => {
    setPolicyToTerminate(undefined);
  };

  return (
    <>
      <h2>{l10n.title.cancelInsurance}</h2>

      {policiesToDisplay?.map((policy: Policy, index: number) => (
        <div className="card flex-row" key={index}>
          <div className="card-body flex-grow w-4/5">
            <div className="font-semibold mb-2">
              {l10n.products[productIdToTypeKeyDict[policy.productTypeId]].name}
            </div>
            <div className="grid gap-1 leading-none">
              <div className="text-base">
                <span>{l10n.label.coveragePerMonth}</span>:&nbsp;
                <span>
                  {Formatters.formatCurrencyNumber(
                    policy.policyDetails.DesiredCoverage ?? policy.policyDetails.Amount,
                    true,
                    undefined,
                    0
                  )}
                </span>
              </div>
              <div className="text-base">
                <span>{l10n.label.grossPrice}</span>:&nbsp;
                <span>{Formatters.formatCurrencyNumber(policy.grossPrice)}</span>
              </div>
              {policy.policyDetails.terminationDate && (
                <div className="mt-4 flex flex-col justify-center items-center text-center text-base font-medium">
                  <span>{l10n.label.durationOfInsurance}:</span>
                  <span>
                    {l10n.common.until}&nbsp;
                    {Formatters.formatShortDate(policy.policyDetails.terminationDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
          {!policy?.policyDetails?.terminationDate && (
            <div className="p-card-body w-1/5 flex justify-end items-center overflow-hidden rounded-r-card">
              <button
                className="btn-action !m-0 w-9 h-9"
                onClick={() => setPolicyToTerminate(policy)}
              >
                <PowerOutline className="w-5 h-5 stroke-[32px] stroke-danger" />
              </button>
            </div>
          )}
        </div>
      ))}

      {policyToTerminate && (
        <Modal backgroundTransparency={60} onClose={closeModal}>
          {policyToTerminate.termination ? (
            <>
              <div className="modal-body">
                <div className="w-12 h-12 p-2 inline-flex justify-center items-center bg-primary rounded-3xl">
                  <Warning className={"w-8 h-8 text-secondary stroke-1"} />
                </div>
                <div className="text-base text-center">
                  {!policyToTerminate.termination.isImmediate ? (
                    <span>
                      {
                        l10n.notice
                          .youAreInTheProcessOfCancellingInsurancePleaseNoteEndOfInsuranceTermListedBelow
                      }
                    </span>
                  ) : (
                    <span>
                      {
                        l10n.notice
                          .youAreCancellingInsuranceRetroactivelyPleaseNoteCancellationTakesEffectImmediately
                      }
                    </span>
                  )}
                </div>
                {!policyToTerminate.termination.isImmediate && (
                  <div className="w-full p-2 text-center">
                    <div className="text-xl font-bold pb-4">
                      {l10n.products[productIdToTypeKeyDict[policyToTerminate.productTypeId]].name}
                    </div>
                    <div className="p-2 border rounded-md">
                      <div className="text-base">{l10n.notice.insuranceEndsOn}</div>
                      <div className="text-secondary font-semibold">
                        {/* TODO: What if `earliestExecutionDate` isn't there? */}
                        {Formatters.formatShortDate(
                          policyToTerminate.termination.earliestExecutionDate ??
                            new Date().addDays(15)
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {!policyToTerminate.termination.isImmediate ? (
                  <div className="text-base text-center font-semibold">
                    {l10n.notice.areYouSureYouWantCancelInsurance}
                  </div>
                ) : (
                  <div className="text-base text-center font-semibold">
                    {l10n.notice.areYouSureYouWantExerciseRightOfCancellation}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                {isTerminationFailed && (
                  <div className="notification notification-danger mb-3">
                    <InfoCircle className="w-[24px] h-[24px] shrink-0 fill-danger" />
                    {l10n.notice.somethingWentWrongPleaseTryAgain}
                  </div>
                )}
                <button className="btn btn-primary" onClick={() => closeModal()}>
                  {l10n.common.no}
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={isTerminationLoading}
                  onClick={() => terminatePolicy(policyToTerminate)}
                >
                  {isTerminationLoading ? l10n.common.loading : l10n.btn.yesCancel}
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col justify-center items-center">{l10n.common.loading}</div>
          )}
        </Modal>
      )}
    </>
  );
};
